import React from 'react'
import { Container, Form, Row, Col, Button } from 'react-bootstrap'
import { find } from '@src/lib/lodash'
import './../style.scss'
import { CarService, UserService } from '@src/domain/services'
import { z } from 'zod'
import { useForm } from '@src/lib/react-hook-form'
import { SearchCondition } from '../report.view-model'
import _ from 'lodash'
import { FaSearch, PiMicrosoftExcelLogoBold } from '@src/lib/react-icons'
import { defaultDateTime } from '@src/utils'

interface CarRecordsSaerchBarProps {
  factoryOptions: UserService.FactoriesBo;
  carModelOptions: CarService.CarModelsBo;
  searchCondition: SearchCondition;
  searchOnClick: (condition: SearchCondition) => void;
  downloadOnClick: (condition: SearchCondition) => void;
}

export const CarRecordsSaerchBar: React.FC<CarRecordsSaerchBarProps> = ({factoryOptions, carModelOptions, searchCondition, searchOnClick, downloadOnClick}) => {
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<SearchCondition>({
    reValidateMode: 'onSubmit',
    defaultValues: {...searchCondition, ...defaultDateTime},
    rules: z.object({
      factoryUuid: z.string().nonempty({ message: '請選擇廠區' }),
      category: z.string().nonempty({ message: '請選擇類別' }),
      fromDate: z.string().optional(),
      fromTime: z.string().optional(),
      toDate: z.string().optional(),
      toTime: z.string().optional(),
      carModelUuid: z.string().optional(),
      engineNo: z.string().optional(),
      sfxNo: z.string().optional(),
      employee: z.string().optional(),
      stationUuid: z.string().optional(),
      status: z.string().optional()
    })
  })

  const factoryUuid = watch('factoryUuid')

  const factoryDto = find(factoryOptions, { uuid: factoryUuid })

  const stationOptions = factoryDto?.stations || []

  const category = watch('category')

  type Status = 'moving' | 'assembly' | 'quality' | 'repair';
  type StatusCode = '1' | '2' | '3' | '9';
  
  interface StatusItem {
    code: StatusCode;
    description: string;
  }
  
  const statusMap: Record<Status, StatusItem[]> = {
    moving: [
      { code: '1', description: '待移車' },
      { code: '2', description: '移車中' },
      { code: '3', description: '已完成' },
      { code: '9', description: '失敗' }
    ],
    assembly: [
      { code: '1', description: '待組裝' },
      { code: '2', description: '組裝中' },
      { code: '3', description: '已完成' },
      { code: '9', description: '失敗' }
    ],
    quality: [
      { code: '1', description: '待品檢' },
      { code: '2', description: '品檢中' },
      { code: '3', description: '已完成' },
      { code: '9', description: '失敗' }
    ],
    repair: [
      { code: '1', description: '待修復' },
      { code: '2', description: '修復中' },
      { code: '3', description: '已完成' },
      { code: '9', description: '失敗' }
    ],
  }
  const _category = category as Status
  const statusOptions = _category ? statusMap[_category] : []

  const handleOnSubmit = (dto: SearchCondition) => {
    searchOnClick(dto)
  }

  const handleOnDownload =(dto: SearchCondition) => {
    downloadOnClick(dto)
  }
  
  const handleTextOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setValue(name, value.toUpperCase(), { shouldValidate: true })
  }

  return (
    <div className='search-bar-wrap'>
      <Container>
        <Form onSubmit={handleSubmit(handleOnSubmit)}>
          <Row className='mb-3'>
            <Form.Group as={Col}>
              <Form.Label>廠區</Form.Label>
              <Form.Select {...register('factoryUuid')}>
                <option value=''>請選擇廠區</option>
                {
                  factoryOptions
                    .filter(o => o.code !== 'ALL')
                    .map(option => <option key={option.uuid} value={option.uuid}>{`${option.code}-${option.description}`}</option>)
                }
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>類別</Form.Label>
              <Form.Select {...register('category')}>
                <option value='moving'>移車</option>
                <option value='assembly'>組裝</option>
                <option value='quality'>品檢</option>
                <option value='repair'>修復</option>
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>起</Form.Label>
              <Form.Group as={Col} className='datetime-wrap'>
                <div className='d-flex'>
                  <Form.Control className='col date' type='date' {...register('fromDate')} />
                  <Form.Control className='col time' type='time' {...register('fromTime')} />
                </div>
              </Form.Group>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>訖</Form.Label>
              <Form.Group as={Col} className='datetime-wrap'>
                <div className='d-flex'>
                <Form.Control className='col date' type='date'  {...register('toDate')}/>
                <Form.Control className='col time' type='time' {...register('toTime')} />
                </div>
              </Form.Group>
            </Form.Group>
          </Row>

          <Row className='mb-3'>
            <Form.Group as={Col}>
              <Form.Label>狀態</Form.Label>
              <Form.Select {...register('status')}>
                <option value=''>請選擇狀態</option>
                {statusOptions.map(option => <option key={option.code} value={option.code}>{`${option.description}`}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>車種</Form.Label>
              <Form.Select {...register('carModelUuid')}>
                <option value=''>請選擇車種</option>
                {
                  carModelOptions.map(option => <option key={option.uuid} value={option.uuid}>{`${option.description}`}</option>)
                }
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>引擎編號</Form.Label>
              <Form.Control {...register('engineNo')} onChange={handleTextOnChange} />
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>SFX</Form.Label>
              <Form.Control {...register('sfxNo')} onChange={handleTextOnChange} />
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>員工</Form.Label>
              <Form.Control {...register('employee')} onChange={handleTextOnChange} />
            </Form.Group>
            {
              category !== 'moving'
                ? <Form.Group as={Col}>
                  <Form.Label>工位</Form.Label>
                  <Form.Select {...register('stationUuid')}>
                    <option value=''>請選擇工位</option>
                    {
                      stationOptions
                        .map(option => <option key={option.uuid} value={option.uuid}>{`${option.code}-${option.description}`}</option>)
                    }
                  </Form.Select>
                </Form.Group>
                : null
            }
          </Row>

          <div className='d-flex gap-2 btn-row'>
            <div className='error-div'>{_(errors).pickBy(v => !_.isEmpty(v)).mapValues(v => v.message).map().join('、')}</div>
            <Button variant='primary' type='submit'>
              <FaSearch />
              查詢
            </Button>
            <Button variant='success' onClick={handleSubmit(handleOnDownload)}>
              <PiMicrosoftExcelLogoBold />
              下載
            </Button>
          </div>
        </Form>
      </Container>
    </div>
  )
}
